import { ContentStatus } from '../entity/api/content';
import { ContentAiFunnelStatus } from '../entity/api/content-funnel';
import {
  generatePrimaryKeywordSocketResSchema,
  generatePrimaryPromptSocketResSchema,
  getArticleSocketResSchema,
} from '../entity/api/routes/article';
import { generateBrandContextSocketResSchema } from '../entity/api/routes/brand';
import { getBulkGroupSocketResSchema } from '../entity/api/routes/bulk-group';
import { getOrganizationSocketResSchema } from '../entity/api/routes/organization';
import { getProductSocketResSchema } from '../entity/api/routes/product';
import { seoStrategyUpdateSocketResSchema } from '../entity/api/routes/seo-strategy';
import { SocketEvent } from '../entity/api/routes/socket';
import { getUserSocketResSchema } from '../entity/api/routes/user';
import { InternalServerError } from '../error';
import { trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';
import { showErrorScreen } from '../services/error-screen';
import {
  type ArticleWithSeo,
  getAuthUserFromCache,
  getStores,
  invalidateOrganizationBilling,
  removeArticleFromCache,
  removeOrganizationFromCache,
  removeUserFromCache,
  setArticleAnalysisInCache,
  setArticleFunnelInCache,
  setArticleInCache,
  setBulkGroupInCache,
  setOrganizationInCache,
  setProductInCache,
  setProductSectionsInCacheForSocket,
  setUserInCache,
} from '../services/storage';
import { NotificationPriority, NotificationType } from '../services/storage/notifications';
import { onContentPromptUpdate } from './ai';
import {
  onArticleGeneratePrimaryKeywordUpdate,
  onArticleGeneratePrimaryPromptUpdate,
} from './article';
import { onBrandGenerateContextUpdate } from './brand';
import { getQueryClient } from './request';
import { onSeoStrategyUpdate } from './seo-strategy';

export type SocketFn = (data: unknown) => Promise<unknown>;
type SocketEventMap = {
  [SocketEvent.STATUS_PING]: SocketFn;
  [SocketEvent.STATUS_PONG]: SocketFn;

  [SocketEvent.CONTENT_UPDATE]: SocketFn;
  [SocketEvent.CONTENT_PROMPT_UPDATE]: SocketFn;
  [SocketEvent.ARTICLE_GENERATE_PRIMARY_KEYWORD]: SocketFn;
  [SocketEvent.ARTICLE_GENERATE_PRIMARY_PROMPT]: SocketFn;
  [SocketEvent.BULK_GROUP_UPDATE]: SocketFn;
  [SocketEvent.PRODUCT_UPDATE]: SocketFn;
  [SocketEvent.BRAND_GENERATE_CONTEXT]: SocketFn;
  [SocketEvent.SEO_STRATEGY_UPDATE]: SocketFn;
  [SocketEvent.ORGANIZATION_UPDATE]: SocketFn;
  [SocketEvent.USER_UPDATE]: SocketFn;
};

// we always need the sockets setup for typing and security
function emptySocket(_data: unknown) {
  // NOTE: do nothing, we need it for typing purposes still
  return Promise.resolve(undefined);
}

export function handleStatusError() {
  showErrorScreen();
  return Promise.resolve(undefined);
}

export function getSocketEventMap(): SocketEventMap {
  return {
    [SocketEvent.STATUS_PING]: emptySocket,
    [SocketEvent.STATUS_PONG]: emptySocket,

    [SocketEvent.CONTENT_UPDATE]: async function (data: unknown) {
      // update the store
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const res = getArticleSocketResSchema.safeParse(parsedData);
        if (!res.success || res.error != null) {
          throw res.error ?? new InternalServerError('update validation was not a success');
        }

        // Get queryClient for TanStack Query cache updates
        const queryClient = getQueryClient();

        // handle updates - use TanStack Query cache helpers which also sync to Zustand
        const addNotificationFn = getStores().notifications.getState().addNotification;
        const currentUser = getAuthUserFromCache(queryClient);
        const currentUserId = currentUser?._id;

        for (let i = 0; i < res.data.list.length; i += 1) {
          const { content, analysis, seoScore, funnel } = res.data.list[i];

          // Update article in TanStack Query cache (also syncs to Zustand)
          const articleWithSeo: ArticleWithSeo = {
            ...content,
            seo: seoScore,
          };
          setArticleInCache(queryClient, articleWithSeo);

          if (analysis != null) {
            // Update analysis in TanStack Query cache (also syncs to Zustand)
            setArticleAnalysisInCache(queryClient, content._id, analysis);
          }

          if (funnel != null) {
            // Update funnel in TanStack Query cache (also syncs to Zustand)
            setArticleFunnelInCache(queryClient, content._id, funnel);

            // Add notification when content generation is complete and current user is the creator
            if (
              funnel.status === ContentAiFunnelStatus.Done &&
              currentUserId != null &&
              content.createdBy === currentUserId
            ) {
              try {
                addNotificationFn({
                  type: NotificationType.ContentGenerationSuccess,
                  createdBy: content.createdBy,
                  contentId: content._id,
                  contentTitle: content.title ?? content.primaryKeyword ?? '',
                  priority: NotificationPriority.Medium,
                  metadata: {
                    brandId: content.brandId,
                    organizationId: content.organizationId,
                  },
                });
              } catch (err) {
                captureException(
                  err,
                  {
                    file: 'socket.router.ts',
                    method: 'CONTENT_UPDATE',
                    infoMessage: `unable to add notification for content generation success`,
                  },
                  trackError
                );
              }
            }
          }

          // in error cases on the generation, a credit is credited back
          // we want to invalidate the organization billing to make sure the sync
          // comes through and updates the credits depending of their state
          if (
            content.status === ContentStatus.AnalysisError ||
            content.status === ContentStatus.RefreshError ||
            funnel?.status === ContentAiFunnelStatus.ReqGenContentError ||
            funnel?.status === ContentAiFunnelStatus.ReqGenOutlinesError ||
            funnel?.status === ContentAiFunnelStatus.ReqGenTitlesError
          ) {
            invalidateOrganizationBilling(queryClient, content.organizationId);
          }
        }

        // Handle deletions - use TanStack Query cache helper which also syncs to Zustand
        for (let i = 0; i < res.data.listDeleted.length; i += 1) {
          const id = res.data.listDeleted[i];
          removeArticleFromCache(queryClient, id);
        }
      } catch (err) {
        captureException(
          err,
          {
            file: 'socket.router.ts',
            method: 'CONTENT_UPDATE',
            infoMessage: `unable to apply content:update socket payload`,
          },
          trackError
        );
      }

      return Promise.resolve(data);
    },

    [SocketEvent.CONTENT_PROMPT_UPDATE]: (data) => Promise.resolve(onContentPromptUpdate(data)),

    [SocketEvent.ARTICLE_GENERATE_PRIMARY_KEYWORD]: async function (data: unknown) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const res = generatePrimaryKeywordSocketResSchema.safeParse(parsedData);

        if (!res.success || res.error != null) {
          return;
        }

        // We notify the pending promise about the result
        onArticleGeneratePrimaryKeywordUpdate(res.data);
      } catch (err) {
        captureException(
          err,
          {
            file: 'socket.router.ts',
            method: 'BRAND_GENERATE_CONTEXT',
            infoMessage: `unable to process brand generate context`,
          },
          trackError
        );
      }

      return Promise.resolve(data);
    },

    [SocketEvent.ARTICLE_GENERATE_PRIMARY_PROMPT]: async function (data: unknown) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const res = generatePrimaryPromptSocketResSchema.safeParse(parsedData);

        if (!res.success || res.error != null) {
          return;
        }

        // We notify the pending promise about the result
        onArticleGeneratePrimaryPromptUpdate(res.data);
      } catch (err) {
        captureException(
          err,
          {
            file: 'socket.router.ts',
            method: 'ARTICLE_GENERATE_PRIMARY_PROMPT',
            infoMessage: `unable to process primary prompt generation`,
          },
          trackError
        );
      }

      return Promise.resolve(data);
    },

    // TODO: maybe we should just trigger a new REST request
    [SocketEvent.ORGANIZATION_UPDATE]: async function (data: unknown) {
      // update the store
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const res = getOrganizationSocketResSchema.safeParse(parsedData);
        if (!res.success || res.error != null) {
          throw res.error ?? new InternalServerError('update validation was not a success');
        }

        // Get queryClient for TanStack Query cache updates
        const queryClient = getQueryClient();

        if ('organization' in res.data) {
          // handle get - update TanStack Query cache
          setOrganizationInCache(queryClient, res.data.organization);
        } else if (res.data.isDeleted) {
          // handle delete - remove from TanStack Query cache
          removeOrganizationFromCache(queryClient, res.data.organizationId);
        }
      } catch (err) {
        // TODO: what to do with the error?
        captureException(
          err,
          {
            file: 'socket.router.ts',
            method: 'ORGANIZATION_UPDATE',
            infoMessage: `unable to update organization`,
          },
          trackError
        );
      }

      return Promise.resolve(data);
    },

    [SocketEvent.PRODUCT_UPDATE]: async function (data: unknown) {
      // update the store
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const res = getProductSocketResSchema.safeParse(parsedData);
        if (!res.success || res.error != null) {
          throw res.error ?? new InternalServerError('update validation was not a success');
        }

        // Get queryClient for TanStack Query cache updates
        const queryClient = getQueryClient();

        for (let i = 0; i < res.data.list.length; i += 1) {
          const { product, sections } = res.data.list[i];

          // Update TanStack Query cache
          setProductInCache(queryClient, product);

          if (sections != null) {
            // Update TanStack Query cache
            setProductSectionsInCacheForSocket(queryClient, product._id, sections);
          }
        }
      } catch (err) {
        // TODO: what to do with the error?
        captureException(
          err,
          {
            file: 'socket.router.ts',
            method: 'PRODUCT_UPDATE',
            infoMessage: `unable to update product`,
          },
          trackError
        );
      }

      return Promise.resolve(data);
    },

    [SocketEvent.BULK_GROUP_UPDATE]: async function (data: unknown) {
      // update the store
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const res = getBulkGroupSocketResSchema.safeParse(parsedData);
        if (!res.success || res.error != null) {
          throw res.error ?? new InternalServerError('update validation was not a success');
        }

        // Get queryClient for TanStack Query cache updates
        const queryClient = getQueryClient();

        if (res.data.list.length > 0) {
          const { bulkGroup } = res.data.list[0];

          // Update TanStack Query cache
          setBulkGroupInCache(queryClient, bulkGroup);
        }
      } catch (err) {
        // TODO: what to do with the error?
        captureException(
          err,
          {
            file: 'socket.router.ts',
            method: 'BULK_GROUP_UPDATE',
            infoMessage: `unable to update bulk group`,
          },
          trackError
        );
      }

      return Promise.resolve(data);
    },

    [SocketEvent.BRAND_GENERATE_CONTEXT]: async function (data: unknown) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const res = generateBrandContextSocketResSchema.safeParse(parsedData);

        if (!res.success || res.error != null) {
          return;
        }

        // We notify the pending promise about the result
        onBrandGenerateContextUpdate(res.data);
      } catch (err) {
        captureException(
          err,
          {
            file: 'socket.router.ts',
            method: 'BRAND_GENERATE_CONTEXT',
            infoMessage: `unable to process brand generate context`,
          },
          trackError
        );
      }

      return Promise.resolve(data);
    },

    [SocketEvent.SEO_STRATEGY_UPDATE]: async function (data: unknown) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const res = seoStrategyUpdateSocketResSchema.safeParse(parsedData);

        if (!res.success || res.error != null) {
          return;
        }

        onSeoStrategyUpdate(res.data);
      } catch (err) {
        captureException(
          err,
          {
            file: 'socket.router.ts',
            method: 'SEO_STRATEGY_UPDATE',
            infoMessage: `unable to process seo strategy update`,
          },
          trackError
        );
      }

      return Promise.resolve(data);
    },

    // TODO: maybe we should just trigger a new REST request
    [SocketEvent.USER_UPDATE]: async function (data: unknown) {
      // update the store
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const res = getUserSocketResSchema.safeParse(parsedData);
        if (!res.success || res.error != null) {
          throw res.error ?? new InternalServerError('update validation was not a success');
        }

        // Get queryClient for TanStack Query cache updates
        const queryClient = getQueryClient();

        if ('user' in res.data) {
          // handle get - update TanStack Query cache
          setUserInCache(queryClient, res.data.user);
        } else if (res.data.isDeleted) {
          // handle delete - remove from TanStack Query cache
          removeUserFromCache(queryClient, res.data.userId);
        }
      } catch (err) {
        // TODO: what to do with the error?
        captureException(
          err,
          {
            file: 'socket.router.ts',
            method: 'USER_UPDATE',
            infoMessage: `unable to update user`,
          },
          trackError
        );
      }

      return Promise.resolve(data);
    },
  };
}
